import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ParentsAuthProvider } from './contexts/ParentsAuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ParentsProtectedRoute from './components/auth/ParentsProtectedRoute';
import LoadingSpinner from './components/ui/LoadingSpinner';
import './index.css';

// Import direto das páginas
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ConfirmationPage from './pages/auth/ConfirmationPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import PDVPage from './pages/dashboard/PDVPage';
import PDVDashboard from './pages/pdv/PDVDashboard';
import PDVReports from './pages/pdv/PDVReports';
import ProductsPage from './pages/dashboard/ProductsPage';
import ProductRegisterPage from './pages/dashboard/ProductRegisterPage';
import StudentsPage from './pages/dashboard/StudentsPage';
import StudentRegisterPage from './pages/dashboard/StudentRegisterPage';
import StudentDetailsPage from './pages/dashboard/StudentDetailsPage';
import StudentEditPage from './pages/dashboard/StudentEditPage';
import ProductDetailsPage from './pages/dashboard/ProductDetailsPage';
import ProductEditPage from './pages/dashboard/ProductEditPage';
import BanksPage from './pages/dashboard/BanksPage';
import BankRegisterPage from './pages/dashboard/BankRegisterPage';
import BankDetailsPage from './pages/dashboard/BankDetailsPage';
import BankEditPage from './pages/dashboard/BankEditPage';
import CashierPage from './pages/dashboard/CashierPage';
import SalesHistoryPage from './pages/dashboard/SalesHistoryPage';
import FinancialReportsPage from './pages/dashboard/FinancialReportsPage';
import MonthlyPaymentsPage from './pages/dashboard/MonthlyPaymentsPage';
import AccountsPayablePage from './pages/dashboard/AccountsPayablePage';
import AccountsReceivablePage from './pages/dashboard/AccountsReceivablePage';
import StudentExtractPage from './pages/dashboard/StudentExtractPage';
import SettingsPage from './pages/dashboard/SettingsPage';
import PeoplePage from './pages/dashboard/PeoplePage';
import PersonRegisterPage from './pages/dashboard/PersonRegisterPage';
import PersonEditPage from './pages/dashboard/PersonEditPage';
import InventoryPage from './pages/dashboard/InventoryPage';
import CalculatorPage from './pages/dashboard/CalculatorPage';
import MenuPage from './pages/dashboard/MenuPage';
import ReviewsPage from './pages/dashboard/ReviewsPage';
import MessagesPage from './pages/dashboard/MessagesPage';
import ParentsLoginPage from './pages/portal/ParentsLoginPage';
import ParentsRegisterPage from './pages/portal/ParentsRegisterPage';
import ParentsPortalOptimized from './pages/portal/ParentsPortalOptimized';
import DashboardLayout from './components/layout/DashboardLayout';

function App() {
  return (
    <AuthProvider>
      <ParentsAuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
        <div className="App">
          <Routes>
            {/* Rota principal - Landing Page */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Rotas de autenticação */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/confirmation" element={<ConfirmationPage />} />
            
            {/* Portal dos Pais */}
            <Route path="/parents-login" element={<ParentsLoginPage />} />
            <Route path="/parents-portal/register" element={<ParentsRegisterPage />} />
            <Route 
              path="/parents-portal" 
              element={
                <ParentsProtectedRoute>
                  <ParentsPortalOptimized />
                </ParentsProtectedRoute>
              } 
            />
            
            {/* Rota PDV - Tela cheia sem sidebar */}
            <Route 
              path="/dashboard/pdv" 
              element={
                <ProtectedRoute>
                  <PDVPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Rotas PDV Dashboard e Relatórios */}
            <Route 
              path="/dashboard/pdv/dashboard" 
              element={
                <ProtectedRoute>
                  <PDVDashboard />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/dashboard/pdv/reports" 
              element={
                <ProtectedRoute>
                  <PDVReports />
                </ProtectedRoute>
              } 
            />
            
            {/* Rotas protegidas - Dashboard (aninhadas) */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/new" element={<ProductRegisterPage />} />
              <Route path="products/:id" element={<ProductDetailsPage />} />
              <Route path="products/:id/edit" element={<ProductEditPage />} />
              <Route path="students" element={<StudentsPage />} />
              <Route path="students/new" element={<StudentRegisterPage />} />
              <Route path="students/:id" element={<StudentDetailsPage />} />
              <Route path="students/:id/edit" element={<StudentEditPage />} />
              <Route path="people" element={<PeoplePage />} />
              <Route path="people/new" element={<PersonRegisterPage />} />
              <Route path="people/edit/:id" element={<PersonEditPage />} />
              <Route path="banks" element={<BanksPage />} />
              <Route path="banks/new" element={<BankRegisterPage />} />
              <Route path="banks/:bankId" element={<BankDetailsPage />} />
              <Route path="banks/:bankId/edit" element={<BankEditPage />} />
              <Route path="cashier" element={<CashierPage />} />
              <Route path="sales" element={<SalesHistoryPage />} />
              <Route path="reports" element={<FinancialReportsPage />} />
              <Route path="monthly-payments" element={<MonthlyPaymentsPage />} />
              <Route path="accounts-payable" element={<AccountsPayablePage />} />
              <Route path="accounts-receivable" element={<AccountsReceivablePage />} />
              <Route path="student-extract" element={<StudentExtractPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="calculator" element={<CalculatorPage />} />
              <Route path="menu" element={<MenuPage />} />
              <Route path="reviews" element={<ReviewsPage />} />
              <Route path="messages" element={<MessagesPage />} />
            </Route>

            {/* Rota 404 */}
            <Route 
              path="*" 
              element={
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
                    <p className="text-xl text-gray-600 mb-8">Página não encontrada</p>
                    <a 
                      href="/" 
                      className="btn-primary px-6 py-3 text-lg"
                    >
                      Voltar ao Início
                    </a>
                  </div>
                </div>
              } 
            />
          </Routes>
          
          {/* Toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 2500,
              style: {
                background: '#363636',
                color: '#fff',
                fontSize: '14px',
                maxWidth: '300px',
                zIndex: 9999,
              },
              success: {
                duration: 2000,
                style: {
                  background: '#22c55e',
                  color: '#fff',
                  fontSize: '14px',
                  maxWidth: '300px',
                },
              },
              error: {
                duration: 3000,
                style: {
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '14px',
                  maxWidth: '300px',
                },
              },
            }}
          />
        </div>
      </Router>
      </ParentsAuthProvider>
    </AuthProvider>
  );
}

export default App;